/**
 * OrderMapView
 * Mapa Leaflet que muestra:
 *   1) La ubicación del cliente (marcador dorado corporativo)
 *   2) El polígono/círculo de la zona geo-referenciada (si existe)
 *   3) Ruta del técnico: puntos donde debe visitar en su turno (si se proveen)
 *
 * Props:
 *   - order:  { lat, lon, address, client_name, code, zone_id, zone_name }
 *   - zone:   { geometry_type: "polygon"|"circle", geometry: {coords[]}|{center,radius_km} }
 *   - technicianRoute: opcional, array de {lat, lon, code, client_name, scheduled_at}
 *   - height: alto en px (default 260)
 */
import { MapContainer, TileLayer, Marker, Polygon, Circle, Popup, Polyline } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { MapPin } from "lucide-react";

// Iconos custom corporativos ─────────────────────────────────
const CLIENT_ICON = L.divIcon({
  className: "",
  html: '<div style="background:#E0E621;color:#0A3747;width:30px;height:30px;border-radius:50%;display:flex;align-items:center;justify-content:center;font-weight:bold;font-size:14px;border:3px solid #0A3747;box-shadow:0 2px 6px rgba(0,0,0,.3)">📍</div>',
  iconSize: [30, 30],
  iconAnchor: [15, 15],
});

const ROUTE_STOP_ICON = (n, isCurrent) =>
  L.divIcon({
    className: "",
    html: `<div style="background:${isCurrent ? "#E0E621" : "#0A3747"};color:${isCurrent ? "#0A3747" : "#fff"};width:26px;height:26px;border-radius:50%;display:flex;align-items:center;justify-content:center;font-weight:bold;font-size:11px;border:2px solid ${isCurrent ? "#0A3747" : "#E0E621"};box-shadow:0 1px 4px rgba(0,0,0,.2)">${n}</div>`,
    iconSize: [26, 26],
    iconAnchor: [13, 13],
  });

export default function OrderMapView({ order, zone, technicianRoute = [], height = 260 }) {
  // Ecuador está sobre la línea ecuatorial (lat 0), no tratar 0 como ausente
  const hasCoords = order && order.lat != null && order.lon != null;
  if (!hasCoords) {
    return (
      <div
        className="flex flex-col items-center justify-center border border-dashed border-gray-300 bg-gray-50 rounded-md text-gray-400 text-xs gap-1.5"
        style={{ height }}
        data-testid="order-map-empty"
      >
        <MapPin size={22} className="opacity-50" />
        Sin coordenadas geolocalizadas
      </div>
    );
  }

  const center = [order.lat, order.lon];

  // Preparar geometría de zona
  const polygonPositions =
    zone?.geometry_type === "polygon" && Array.isArray(zone?.geometry?.coords)
      ? zone.geometry.coords.map((p) => (Array.isArray(p) ? [p[0], p[1]] : [p.lat, p.lon])).filter(Boolean)
      : null;

  const circleCenter =
    zone?.geometry_type === "circle" && zone?.geometry?.center
      ? Array.isArray(zone.geometry.center)
        ? zone.geometry.center
        : [zone.geometry.center.lat, zone.geometry.center.lon]
      : null;
  const circleRadiusKm = zone?.geometry?.radius_km || 0;

  // Ruta del técnico: incluir la OT actual y otros stops (lat/lon=0 es válido, Ecuador)
  const routeStops = technicianRoute.length
    ? technicianRoute.filter((s) => s.lat != null && s.lon != null).sort((a, b) => {
        if (a.scheduled_at && b.scheduled_at) return new Date(a.scheduled_at) - new Date(b.scheduled_at);
        return 0;
      })
    : [];
  const routeLine = routeStops.length > 1 ? routeStops.map((s) => [s.lat, s.lon]) : null;

  return (
    <div className="rounded-md overflow-hidden border border-gray-200 relative" data-testid="order-map">
      <MapContainer
        center={center}
        zoom={13}
        style={{ height, width: "100%" }}
        scrollWheelZoom={false}
      >
        <TileLayer
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          attribution="&copy; OpenStreetMap"
        />

        {/* Zona: polígono */}
        {polygonPositions && polygonPositions.length >= 3 && (
          <Polygon
            positions={polygonPositions}
            pathOptions={{
              color: "#0A3747",
              weight: 2.5,
              fillColor: "#E0E621",
              fillOpacity: 0.15,
              dashArray: "6 4",
            }}
          >
            <Popup>
              <div className="font-semibold text-xs">{order.zone_name || "Zona operativa"}</div>
              <div className="text-[10px] text-gray-500">Polígono · {polygonPositions.length} vértices</div>
            </Popup>
          </Polygon>
        )}

        {/* Zona: círculo */}
        {circleCenter && circleRadiusKm > 0 && (
          <Circle
            center={circleCenter}
            radius={circleRadiusKm * 1000}
            pathOptions={{
              color: "#0A3747",
              weight: 2.5,
              fillColor: "#E0E621",
              fillOpacity: 0.15,
              dashArray: "6 4",
            }}
          >
            <Popup>
              <div className="font-semibold text-xs">{order.zone_name || "Zona operativa"}</div>
              <div className="text-[10px] text-gray-500">Radio {circleRadiusKm} km</div>
            </Popup>
          </Circle>
        )}

        {/* Línea de ruta del técnico */}
        {routeLine && (
          <Polyline
            positions={routeLine}
            pathOptions={{
              color: "#0A3747",
              weight: 3,
              opacity: 0.65,
              dashArray: "3 6",
            }}
          />
        )}

        {/* Paradas del técnico (numeradas por orden de agendamiento) */}
        {routeStops.map((s, idx) => {
          const isCurrent = s.code === order.code || s.id === order.id;
          return (
            <Marker
              key={s.id || s.code || idx}
              position={[s.lat, s.lon]}
              icon={ROUTE_STOP_ICON(idx + 1, isCurrent)}
            >
              <Popup>
                <div className="text-xs">
                  <div className="font-mono text-[10px] text-gray-500">{s.code || `Parada ${idx + 1}`}</div>
                  <div className="font-semibold">{s.client_name || "—"}</div>
                  {s.scheduled_at && (
                    <div className="text-[10px] text-gray-600 mt-1">
                      {new Date(s.scheduled_at).toLocaleString("es-EC", { timeZone: "America/Guayaquil", dateStyle: "short", timeStyle: "short" })}
                    </div>
                  )}
                  {isCurrent && (
                    <div className="mt-1 inline-block px-1.5 py-0.5 rounded bg-[#E0E621] text-[#0A3747] text-[9px] font-bold uppercase">
                      Esta orden
                    </div>
                  )}
                </div>
              </Popup>
            </Marker>
          );
        })}

        {/* Marcador principal: cliente de esta OT (si no está ya en la ruta) */}
        {!routeStops.some((s) => s.code === order.code || s.id === order.id) && (
          <Marker position={center} icon={CLIENT_ICON}>
            <Popup>
              <div className="text-xs">
                <div className="font-mono text-[10px] text-gray-500">{order.code}</div>
                <div className="font-semibold">{order.client_name || "Cliente"}</div>
                {order.address && <div className="text-[10px] text-gray-600 mt-1">{order.address}</div>}
              </div>
            </Popup>
          </Marker>
        )}
      </MapContainer>

      {/* Leyenda */}
      <div className="absolute bottom-2 left-2 bg-white/95 backdrop-blur rounded shadow px-2 py-1.5 text-[10px] font-mono flex flex-wrap gap-x-3 gap-y-0.5 z-[400]">
        <span className="flex items-center gap-1">
          <span className="w-3 h-3 rounded-full bg-[#E0E621] border border-[#0A3747]" /> Cliente
        </span>
        {(polygonPositions || circleCenter) && (
          <span className="flex items-center gap-1">
            <span className="w-3 h-3 border-2 border-[#0A3747] bg-[#E0E621]/20" style={{ borderStyle: "dashed" }} /> Zona
          </span>
        )}
        {routeStops.length > 1 && (
          <span className="flex items-center gap-1">
            <span className="w-3 h-3 rounded-full bg-[#0A3747] border-2 border-[#E0E621]" /> Ruta técnico ({routeStops.length})
          </span>
        )}
      </div>
    </div>
  );
}
